import { useEffect, useState } from "react";
import { Waves, Droplets, Sun, Newspaper, Music, Palmtree, Mountain, BookOpen, Landmark, Clock } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";

const links = [
  {
    icon: Clock,
    label: "Right Now",
    shortLabel: "Now",
    to: "/right-now",
    gradient: "from-violet-400 to-fuchsia-600",
    glow: "shadow-fuchsia-500/40",
  },
  {
    icon: Waves,
    label: "Surf Report",
    shortLabel: "Surf",
    to: "/surf",
    gradient: "from-sky-400 to-blue-600",
    glow: "shadow-blue-500/40",
  },
  {
    icon: Droplets,
    label: "Tides",
    to: "/tides",
    gradient: "from-cyan-400 to-teal-600",
    glow: "shadow-teal-500/40",
  },
  {
    icon: Sun,
    label: "Weather",
    to: "/weather",
    gradient: "from-amber-400 to-orange-500",
    glow: "shadow-orange-500/40",
  },
  {
    icon: Palmtree,
    label: "Beaches",
    to: "/beaches",
    gradient: "from-yellow-400 to-amber-500",
    glow: "shadow-amber-500/40",
  },
  {
    icon: Newspaper,
    label: "Blog",
    to: "/blog",
    gradient: "from-emerald-400 to-green-600",
    glow: "shadow-emerald-500/40",
  },
  {
    icon: Mountain,
    label: "Trails",
    to: "/trails",
    gradient: "from-lime-400 to-emerald-600",
    glow: "shadow-lime-500/40",
  },
  {
    icon: Landmark,
    label: "Culture",
    to: "/culture",
    gradient: "from-stone-400 to-amber-700",
    glow: "shadow-amber-700/40",
  },
  {
    icon: BookOpen,
    label: "Guides",
    to: "/guides",
    gradient: "from-indigo-400 to-violet-600",
    glow: "shadow-violet-500/40",
  },
];

interface QuickLinksSectionProps {
  sticky?: boolean;
}

const QuickLinksSection = ({ sticky = false }: QuickLinksSectionProps) => {
  const { pathname } = useLocation();
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    if (!sticky) return;
    setHidden(false);
    let ticking = false;
    let lastY = window.scrollY;
    const update = () => {
      const y = window.scrollY;
      if (y <= 80) setHidden(false);
      else if (y > lastY + 6) setHidden(true);
      else if (y < lastY - 6) setHidden(false);
      lastY = y;
    };
    const raf = requestAnimationFrame(() => {
      lastY = window.scrollY;
      if (window.scrollY <= 80) setHidden(false);
    });
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        update();
        ticking = false;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
    };
  }, [sticky, pathname]);


  return (
    <section
      className={cn(
        "border-b border-border",
        sticky
          ? "sticky top-20 z-30 px-4 py-3 backdrop-blur-md bg-background/85 supports-[backdrop-filter]:bg-background/70 shadow-sm transition-transform duration-300 will-change-transform"
          : "bg-gradient-to-b from-secondary/40 via-background to-background py-8 px-4 sm:py-12 sm:px-6",
        sticky && hidden && "-translate-y-[calc(100%+5rem)]",
      )}
    >
      <div className="mx-auto max-w-5xl">
        <p
          className={cn(
            "text-center font-body text-[10px] sm:text-xs font-medium uppercase tracking-[0.3em] text-muted-foreground mb-5 sm:mb-8 animate-fade-in",
            sticky && "sr-only",
          )}
        >
          Jump to what you need
        </p>
        <div
          className={cn(
            "grid grid-cols-3 sm:grid-cols-5",
            sticky ? "lg:grid-cols-9 gap-2 sm:gap-3" : "lg:grid-cols-9 gap-2.5 sm:gap-4",
          )}
        >
          {links.map((link, i) => {
            const active = pathname === link.to || pathname.startsWith(link.to + "/");
            return (
              <Link
                key={link.label}
                to={link.to}
                aria-current={active ? "page" : undefined}
                style={
                  sticky
                    ? undefined
                    : { animationDelay: `${0.06 * i}s`, animationFillMode: "backwards" }
                }
                className={cn(
                  "group relative flex items-center overflow-hidden transition-all duration-300 mt-0.5",
                  sticky
                    ? "flex-row items-center gap-1 rounded-lg border px-2 py-1.5 hover:shadow-md hover:shadow-primary/10"
                    : "flex-col gap-2 sm:gap-3 rounded-xl sm:rounded-2xl border p-3 sm:p-5 hover:-translate-y-1.5 hover:shadow-xl animate-fade-in",
                  (link as { hideOnMobile?: boolean }).hideOnMobile && "hidden lg:flex",
                  active
                    ? "bg-primary/5 border-primary shadow-md shadow-primary/10 ring-1 ring-primary/30"
                    : "bg-card border-border hover:border-transparent",
                  !sticky && !active && `hover:${link.glow}`,
                )}
              >
                {!sticky && (
                  <div
                    className={cn(
                      "absolute inset-0 bg-gradient-to-br opacity-0 transition-opacity duration-300 group-hover:opacity-100",
                      link.gradient,
                    )}
                    aria-hidden="true"
                  />
                )}
                <div
                  className={cn(
                    "relative flex flex-shrink-0 items-center justify-center transition-all duration-300 text-white shadow-md bg-gradient-to-br",
                    link.gradient,
                    link.glow,
                    sticky
                      ? "h-7 w-7 rounded-md group-hover:scale-110"
                      : "h-12 w-12 sm:h-16 sm:w-16 rounded-xl sm:rounded-2xl shadow-lg group-hover:scale-110 group-hover:rotate-3 group-hover:bg-white/20 group-hover:shadow-xl",
                  )}
                >
                  <link.icon
                    className={cn(
                      "transition-transform drop-shadow-sm",
                      sticky ? "h-3.5 w-3.5" : "h-6 w-6 sm:h-8 sm:w-8",
                    )}
                  />
                </div>
                <span
                  className={cn(
                    "relative font-body font-semibold transition-colors min-w-0",
                    sticky
                      ? "flex-1 text-[11px] sm:text-xs leading-none whitespace-nowrap text-left"
                      : "text-sm sm:text-base text-center leading-tight whitespace-normal break-words min-h-[2.5rem] flex items-center justify-center",
                    active ? "text-primary" : "text-foreground",
                    !sticky && "group-hover:text-white",
                  )}
                >
                  {sticky ? (link as { shortLabel?: string }).shortLabel ?? link.label : link.label}
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default QuickLinksSection;
